/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Data, Router } from '@angular/router';
import cloneDeep from 'lodash-es/cloneDeep';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { FieldDef } from 'app/core/models';
import { Automation, AutomationsResponse } from '../automations.types';
import { AutomationsService } from '../automations.service';
import { getAllowedFields } from 'app/shared/functions';
import { RelatedToType } from 'app/shared/enums';
import { AccountFoldersService } from 'app/modules/folders/folders.service';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { filter, noop, Observable, tap } from 'rxjs';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { Actions, ofActionCompleted, Select, Store } from '@ngxs/store';
import { Automation as AutomationActions } from '../state/actions';
import { AutomationState } from '../state/state';
import { Automation as AutomationModel } from '../builder/builder.types';

@Component({
  selector: 'automations-settings-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomationsSettingsListComponent extends OnDestroyMixin implements OnInit {
  automationSettings: Automation[];
  automationSettingsTableColumns: string[];
  isLoading: boolean;
  automationCount: number = null;
  automations$: Observable<Automation[]>;

  @Select(AutomationState.getAutomations) automationsState$: Observable<Automation[]>;

  private _availableFieldDefs: FieldDef[] = [];
  private _allFieldDefs: FieldDef[] = [];
  private _fileResources: FileResource[];

  private get _accountId(): number {
    return Number(this._accountService.getCurrentAccount().id);
  }

  constructor(
    private readonly _accountService: AccountService,
    private readonly _automationsService: AutomationsService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _snackBar: MatSnackBar,
    private readonly accountFoldersService: AccountFoldersService,
    private readonly confirmService: ConfirmService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly actions$: Actions,
  ) {
    super();

    this.automationSettingsTableColumns = ['name', 'options'];
    this.isLoading = true;
    this.accountFoldersService.accountId = this._accountId.toString();
  }

  ngOnInit(): void {
    this.store.dispatch(new AutomationActions.Get());

    this.automations$ = this.automationsState$.pipe(
      untilComponentDestroyed(this),
      tap((automations) => {
        if (automations) {
          this.automationCount = automations.length;
        }
      }),
    );

    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.setUpAvailableFieldDefs(cloneDeep(data));
      this.setupAvailableFilesForUnderwriting();
    });

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(AutomationActions.Get),
        tap(() => {
          this.isLoading = false;
          this._changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(AutomationActions.Delete),
        tap(() => {
          this._snackBar.open('Automation has been deleted.', '', { duration: 3000 });
          this.store.dispatch(new AutomationActions.Get());
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(AutomationActions.Create),
        tap(() => {
          this._snackBar.open('Automation has been duplicated.', '', { duration: 3000 });
          const automation = this.store.selectSnapshot(AutomationState.getSelectedAutomation);
          this.router.navigate(['builder', automation.id], { relativeTo: this._activatedRoute });
        }),
      )
      .subscribe(noop);
  }

  refreshAutomationsData(response: AutomationsResponse): void {
    this.automationSettings = response.automations;
    this.isLoading = false;
    this._changeDetectorRef.markForCheck();
  }

  // Get all select field defs
  setUpAvailableFieldDefs(data: Data): void {
    this._allFieldDefs = getAllowedFields(data?.fieldDefs, RelatedToType.Project);

    if (this._allFieldDefs) {
      const standardFieldDefs = this._allFieldDefs.filter((fieldDef: FieldDef) => !fieldDef.meta?.is_custom);
      const customFieldDefs = this._allFieldDefs.filter((fieldDef: FieldDef) => fieldDef.meta?.is_custom);

      standardFieldDefs.forEach((fieldDef: FieldDef) => {
        if (fieldDef.meta?.filtering?.operators && fieldDef.type === 'select') {
          fieldDef.meta.options = data.options[fieldDef.meta?.option_name]?.filter((option: any) => !option?.hidden);
          this._availableFieldDefs.push(fieldDef);
        }
      });

      customFieldDefs.forEach((fieldDef: FieldDef) => {
        if (fieldDef.meta?.filtering?.operators && fieldDef.type === 'select') {
          this._availableFieldDefs.push(fieldDef);
        }
      });

      this._availableFieldDefs.forEach((fieldDef: FieldDef) => {
        if (fieldDef?.meta?.options) {
          fieldDef.meta.options = fieldDef.meta.options.map((option: any) => {
            return {
              id: option?.id?.toString() ?? option,
              name: option?.id?.toString() ? option?.name : option,
            };
          });
        }
      });
    }
  }

  setupAvailableFilesForUnderwriting(): void {
    this.accountFoldersService
      .getAllFiles()
      .pipe(untilComponentDestroyed(this))
      .subscribe((fileResources) => {
        this._fileResources = fileResources;
        this._fileResources = this._fileResources.filter(
          (option) =>
            option.name.toLowerCase().indexOf('.xlsxb') > -1 ||
            option.name.toLowerCase().indexOf('.xlsx') > -1 ||
            option.name.toLowerCase().indexOf('.xls') > -1 ||
            option.name.toLowerCase().indexOf('.xlsm') > -1,
        );
      });
  }

  automationToggle(event: MatSlideToggleChange, automation: Automation): void {
    this._automationsService
      .update(this._accountId, { id: automation.id, is_active: event.checked })
      .pipe(untilComponentDestroyed(this))
      .subscribe((response) => {
        automation.is_active = response?.automation?.is_active;
        const msg = response?.automation?.is_active
          ? 'Automation has been activated.'
          : 'Automation has been deactivated.';
        this._snackBar.open(msg, '', { duration: 3000 });
        this._changeDetectorRef.markForCheck();
      });
  }

  editAutomation(automationSetting: Automation): void {
    this.store.dispatch(new AutomationActions.GetById(automationSetting.id));
    this.router.navigate(['builder', automationSetting.id], { relativeTo: this._activatedRoute });
  }

  addAutomation(): void {
    this.router.navigate(['builder'], { relativeTo: this._activatedRoute });
  }

  duplicateAutomation(automationSetting: Automation): void {
    this.store.dispatch(
      new AutomationActions.Create({
        ...automationSetting,
        name: `${automationSetting.name} - Copy`,
        is_active: false,
      } as unknown as AutomationModel),
    );
  }

  removeAutomation(automationSetting: Automation): void {
    this.confirmService
      .openConfirmationModal({ message: `Are you sure that you wish to delete this automation?` })
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
      )
      .subscribe(() => this.store.dispatch(new AutomationActions.Delete(automationSetting.id)));
  }
}
